import { Grid } from '@cmg/design-system';
import { IntlShape, useIntl } from '@cmg/i18n';
import capitalize from 'lodash/capitalize';
import React from 'react';

import { ConvertibleFilingTalkRanges } from '../../../../../../../../common/hooks/useConvertibleFilingTalkRanges';
import {
  InstitutionalIndicationOrderType,
  InterestLevelInterestUnit,
  InterestLevelLimitType,
  OfferingType,
} from '../../../../../../../../graphql';
import { limitTypeLabels } from '../../../../../../../../types/domain/order-book/constants';
import { formatConvertibleInterestLevelPrice } from '../../../../../../../order-book/utils';
import {
  UpdateHistory_CoveredIndicationVersionPartsFragment,
  UpdateHistory_IndicationVersionPartsFragment,
  UpdateHistory_MyIndicationVersionPartsFragment,
} from '../../graphql';

export type Props = Readonly<{
  offeringType: OfferingType | null;
  item:
    | UpdateHistory_IndicationVersionPartsFragment
    | UpdateHistory_CoveredIndicationVersionPartsFragment
    | UpdateHistory_MyIndicationVersionPartsFragment;
  currency: string;
  convertibleTalkRanges: ConvertibleFilingTalkRanges;
  isIndicationPriceAsPercentageParEnabled: boolean | null;
}>;

type GetLimitTypeCellValueArgs = {
  orderType: InstitutionalIndicationOrderType;
  limitType: InterestLevelLimitType | null | undefined;
  currency: string | null | undefined;
  limitPrice: number | null | undefined;
  intl: IntlShape;
};
export const getLimitTypeCellValue = ({
  intl,
  orderType,
  limitType,
  currency,
  limitPrice,
}: GetLimitTypeCellValueArgs) => {
  if (orderType === InstitutionalIndicationOrderType.Market) {
    return;
  }
  const isLimitTypePrice = limitType === InterestLevelLimitType.Price;

  const formattedCurrency = isLimitTypePrice ? ` (${currency})` : '';
  const formattedLimitPrice = isLimitTypePrice
    ? intl.formatNumber(limitPrice, { format: 'currency', currency: currency ?? 'USD' })
    : intl.formatNumber(limitPrice, { format: 'percent' });

  return `${limitTypeLabels[limitType!]}${formattedCurrency}: ${formattedLimitPrice}`;
};

const InterestLevelGridRows: React.FC<Props> = ({
  offeringType,
  item,
  currency,
  convertibleTalkRanges,
  isIndicationPriceAsPercentageParEnabled,
}) => {
  const intl = useIntl();

  return (
    <React.Fragment>
      {item.interestLevels.reduce<React.ReactElement[]>((gridRows, interestLevel, index) => {
        gridRows.push(
          <Grid
            item
            xs={4}
            key={`${item.id}_interest-quantity_${index}`}
            aria-label="interest-quantity"
          >
            {interestLevel.interestUnit === InterestLevelInterestUnit.Currency &&
              intl.formatNumber(interestLevel.interestQuantity, { format: 'currency', currency })}
            {interestLevel.interestUnit === InterestLevelInterestUnit.Percent &&
              intl.formatNumber(interestLevel.interestQuantity, { format: 'percent' })}
            {interestLevel.interestUnit === InterestLevelInterestUnit.Shares &&
              intl.formatNumber(interestLevel.interestQuantity, { format: 'integer' })}
            {interestLevel.interestUnit === InterestLevelInterestUnit.Bonds &&
              intl.formatNumber(interestLevel.interestQuantity, { format: 'integer' })}
          </Grid>
        );
        gridRows.push(
          <Grid item xs={4} key={`${item.id}_interest-level_${index}`} aria-label="interest-unit">
            {capitalize(interestLevel.interestUnit)}
          </Grid>
        );
        gridRows.push(
          <Grid item xs={4} key={`${item.id}_interest-price_${index}`} aria-label="interest-price">
            {offeringType === OfferingType.Convertible ? (
              formatConvertibleInterestLevelPrice({
                intl,
                interestLevel,
                isIndicationPriceAsPercentageParEnabled: !!isIndicationPriceAsPercentageParEnabled,
                convertibleTalkRanges,
              })
            ) : (
              <React.Fragment>
                {capitalize(interestLevel.limitType ? '' : InstitutionalIndicationOrderType.Market)}

                {interestLevel.limitType &&
                  getLimitTypeCellValue({
                    orderType: item.type,
                    limitType: interestLevel.limitType,
                    currency,
                    limitPrice: interestLevel.limitPrice,
                    intl,
                  })}
              </React.Fragment>
            )}
          </Grid>
        );
        return gridRows;
      }, [])}
    </React.Fragment>
  );
};

export default InterestLevelGridRows;
